import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import { shopApi, INR } from '../lib/shop';
import { SEGMENTS, THEMES, TYPES, COLLECTION_LABEL, OFFER_LABEL } from '../lib/catalog';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import ProductCard from '../components/ProductCard';
import '../styles/shop.css';

const CATEGORY_LABEL = { men: 'Men', women: 'Women', unisex: 'Unisex' };
const SORTS = [['featured', 'Popularity'], ['newest', 'Newest First'], ['price-asc', 'Price: Low to High'], ['price-desc', 'Price: High to Low']];

const Radio = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`w-full flex items-center gap-2 py-1.5 text-sm text-left ${active ? 'font-bold text-[var(--si-blue)]' : 'text-[var(--si-black)] hover:text-[var(--si-blue)]'}`}>
    <span className={`w-4 h-4 shrink-0 rounded-full border-2 ${active ? 'border-[var(--si-blue)] bg-[var(--si-blue)] shadow-[inset_0_0_0_2px_#fff]' : 'border-[#9CA3AF]'}`} />
    {children}
  </button>
);

const Group = ({ title, children }) => (
  <div><p className="text-xs font-bold uppercase tracking-wider text-[var(--si-grey)] mb-2">{title}</p>{children}</div>
);

const Filters = ({ slug, params, setParam }) => (
  <div className="space-y-6" data-testid="shop-filters">
    <Group title="Category">
      <Link to={`/shop?${params}`} className={`block py-1.5 text-sm ${!slug ? 'font-bold text-[var(--si-blue)]' : 'hover:text-[var(--si-blue)]'}`} data-testid="filter-all">All</Link>
      {Object.entries({ ...CATEGORY_LABEL, ...COLLECTION_LABEL }).map(([k, l]) => (
        <Link key={k} to={`/shop/${k}?${params}`} data-testid={`filter-${k}`}
          className={`block py-1.5 text-sm ${slug === k ? 'font-bold text-[var(--si-blue)]' : 'hover:text-[var(--si-blue)]'}`}>{l}</Link>
      ))}
    </Group>
    <Group title="Price store">
      <Radio active={!params.get('segment')} onClick={() => setParam('segment', '')} testid="filter-segment-all">Any price</Radio>
      {SEGMENTS.map((s) => (
        <Radio key={s.price} active={params.get('segment') === String(s.price)} onClick={() => setParam('segment', s.price)} testid={`filter-segment-${s.price}`}>
          {INR(s.price)} {s.offer && <span className="text-[11px] font-bold text-[var(--si-green)]">({OFFER_LABEL[s.offer]})</span>}
        </Radio>
      ))}
    </Group>
    <Group title="Type">
      <Radio active={!params.get('type')} onClick={() => setParam('type', '')} testid="filter-type-all">All types</Radio>
      {TYPES.map(([k, l]) => <Radio key={k} active={params.get('type') === k} onClick={() => setParam('type', k)} testid={`filter-type-${k}`}>{l}</Radio>)}
    </Group>
    <Group title="Story collection">
      <Radio active={!params.get('theme')} onClick={() => setParam('theme', '')} testid="filter-theme-all">All</Radio>
      {Object.entries(THEMES).map(([k, t]) => <Radio key={k} active={params.get('theme') === k} onClick={() => setParam('theme', k)} testid={`filter-theme-${k}`}>{t.title}</Radio>)}
    </Group>
  </div>
);

const Shop = () => {
  const { collection: slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const q = params.get('q') || '';
  const segment = params.get('segment') || '';
  const theme = params.get('theme') || '';
  const type = params.get('type') || '';

  const setParam = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n);
  };

  useEffect(() => {
    setLoading(true);
    const args = { sort, q, segment, theme, type, limit: 120 };
    if (slug && CATEGORY_LABEL[slug]) args.category = slug;
    else if (slug) args.collection = slug;
    shopApi.listProducts(args).then(({ products, total }) => {
      setProducts(products || []); setTotal(total || 0); setLoading(false);
    });
  }, [slug, sort, q, segment, theme, type]);

  const title = q ? `Results for "${q}"`
    : theme ? THEMES[theme]?.title
    : segment ? (SEGMENTS.find((s) => String(s.price) === segment)?.title || `${INR(segment)} Store`)
    : CATEGORY_LABEL[slug] || COLLECTION_LABEL[slug] || 'All Products';

  return (
    <div className="si-page">
      <ShopNavbar />
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-6 grid grid-cols-1 md:grid-cols-[250px_1fr] gap-4">
        <aside className={`si-card p-5 h-fit md:sticky md:top-44 md:max-h-[calc(100vh-12rem)] md:overflow-y-auto ${showFilters ? 'block' : 'hidden md:block'}`}>
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--si-line)]">
            <p className="text-base font-bold">Filters</p>
            <button className="md:hidden" onClick={() => setShowFilters(false)} aria-label="Close filters" data-testid="close-filters"><X className="w-5 h-5" /></button>
          </div>
          <Filters slug={slug} params={params} setParam={setParam} />
        </aside>

        <section className="si-card p-3 md:p-5 min-h-[60vh] min-w-0">
          <div className="pb-3 border-b border-[var(--si-line)]">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-xl md:text-2xl font-bold" data-testid="shop-title">{title}</h1>
              <button onClick={() => setShowFilters(true)} className="md:hidden si-btn si-btn-outline !py-2 !px-3 text-sm" data-testid="open-filters">
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
            </div>
            <p className="text-sm text-[var(--si-grey)] mt-1" data-testid="shop-count">Showing {total} product{total === 1 ? '' : 's'}</p>
            <div className="mt-3 flex items-center gap-4 si-scroll-x whitespace-nowrap" data-testid="shop-sort">
              <span className="text-sm font-bold">Sort by</span>
              {SORTS.map(([id, l]) => (
                <button key={id} onClick={() => setSort(id)} data-testid={`sort-${id}`}
                  className={`text-sm pb-1 border-b-2 transition-colors ${sort === id ? 'font-bold text-[var(--si-blue)] border-[var(--si-blue)]' : 'text-[var(--si-black)] border-transparent hover:text-[var(--si-blue)]'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="py-16 text-center text-[var(--si-grey)]" data-testid="shop-loading">Loading products…</p>
          ) : products.length === 0 ? (
            <div className="py-20 text-center" data-testid="shop-empty">
              <p className="text-xl font-bold">No products found</p>
              <p className="mt-2 text-[var(--si-grey)]">Try another filter or <Link to="/shop" className="text-[var(--si-blue)] font-semibold underline">see all products</Link>.</p>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3" data-testid="shop-grid">
              {products.map((p) => <ProductCard key={p.slug} p={p} />)}
            </div>
          )}
        </section>
      </div>
      <ShopFooter />
    </div>
  );
};

export default Shop;
